import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import QueueStartDialog from "@/components/QueueStartDialog";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, PlatformBadge, PLATFORM_LABEL } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, XCircle,
  Ban, FileWarning, Link2, CalendarClock, Copy, Loader2, HardDriveDownload, FolderOpen, Layers, Table2,
} from "lucide-react";

const FIELDS = [
  ["date", "Datum", true], ["time", "Uhrzeit", false], ["series", "Content-Serie", false],
  ["asset_id", "Asset-ID / Dateiname", true], ["platform", "Plattform", true],
  ["caption", "Caption", false], ["link", "Landingpage-Link", false], ["format", "Format", false],
  ["campaign", "Kampagne", false], ["status", "Status", false], ["notes", "Notizen", false],
  ["mode", "Veröffentlichungsmodus", false],
];
const STEPS = ["Upload", "Spalten-Mapping", "Prüfung & Vorschau", "Bestätigung"];

const CountTile = ({ label, value, icon: Icon, tone, testid }) => {
  const tones = {
    neutral: "bg-white border-slate-200 text-slate-800",
    ready: "bg-emerald-50 border-emerald-200 text-emerald-700",
    warn: "bg-amber-50 border-amber-200 text-amber-700",
    block: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${tones[tone] || tones.neutral}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
        <Icon size={15} className="opacity-60" />
      </div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
};

export default function ImportWizard() {
  const { current } = useClients();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [assetSource, setAssetSource] = useState("upload");
  const [defaultPlatform, setDefaultPlatform] = useState("linkedin");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [imp, setImp] = useState(null); // {id, headers, sample, row_count, suggested_mapping}
  const [mapping, setMapping] = useState({});
  const [multi, setMulti] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [usedSheet, setUsedSheet] = useState("");
  const [mode, setMode] = useState("single");
  const [profileName, setProfileName] = useState("");
  const [analysis, setAnalysis] = useState(null); // {counts, rows}
  const [result, setResult] = useState(null);
  const [tiktokImportOpen, setTiktokImportOpen] = useState(false);
  const canConfirmTikTok = ["owner", "admin"].includes(user?.role) && !user?.review_access;

  useEffect(() => {
    if (!current) return;
    api.get(`/projects?client_id=${current.id}`).then(({ data }) => {
      setProjects(data);
      if (data[0]) setProjectId(data[0].id);
    });
    api.get(`/import-profiles?client_id=${current.id}`).then(({ data }) => setProfiles(data));
  }, [current]);

  const doUpload = async () => {
    if (!file) { toast.error("Bitte eine Datei wählen"); return; }
    if (!projectId) { toast.error("Bitte ein Projekt wählen"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImp(data);
      setMapping(data.suggested_mapping || {});
      setMulti(data.multi || null);
      setSheetNames(data.sheet_names || []);
      setUsedSheet(data.used_sheet || "");
      const hasMulti = data.multi?.platform_captions && Object.keys(data.multi.platform_captions).length > 0;
      setMode(hasMulti ? "multi" : "single");
      setStep(1);
      toast.success(`${data.row_count} Zeilen erkannt${hasMulti ? " · Multi-Plattform-Liste" : ""}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyProfile = (pid) => {
    const p = profiles.find((x) => x.id === pid);
    if (p) { setMapping(p.mapping); toast.success(`Profil '${p.name}' angewendet`); }
  };

  const switchSheet = async (sheet) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${imp.id}/sheet?sheet=${encodeURIComponent(sheet)}`);
      setImp({ ...imp, ...data });
      setMapping(data.suggested_mapping || {});
      setMulti(data.multi || null);
      setUsedSheet(data.used_sheet || sheet);
      const hasMulti = data.multi?.platform_captions && Object.keys(data.multi.platform_captions).length > 0;
      setMode(hasMulti ? "multi" : "single");
      toast.success(`Blatt „${sheet}" geladen · ${data.row_count} Zeilen`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doAnalyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${imp.id}/analyze`, {
        client_id: current.id, project_id: projectId, mapping,
        default_platform: defaultPlatform, mode,
        save_profile_name: profileName || null,
      });
      setAnalysis(data);
      setStep(2);
      if (profileName) { api.get(`/import-profiles?client_id=${current.id}`).then(({ data }) => setProfiles(data)); }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${imp.id}/confirm`);
      setResult(data);
      setStep(3);
      if (data.created_tiktok_ready > 0 && canConfirmTikTok) setTiktokImportOpen(true);
      toast.success(`${data.created} Posts erstellt`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const c = analysis?.counts || {};

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  return (
    <div className="pb-24">
      {tiktokImportOpen && imp && result && <QueueStartDialog
        clientId={result.client_id || current.id} projectId={result.project_id || projectId} importId={imp.id} prepareOnly
        onClose={() => setTiktokImportOpen(false)}
        onDone={summary => setResult(previous => ({ ...previous, tiktok_prepared: summary?.tiktok || 0, tiktok_pending: summary?.pendingTikTok || 0 }))} />}
      <PageHeader overline={current.name} title="Bulk-Import" subtitle="Redaktionsliste hochladen, Assets matchen, prüfen und in einem Arbeitsgang planen." />

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 flex-wrap">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div data-testid={`wizard-step-${i}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
              i === step ? "bg-[#0A0A0A] text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-white border border-slate-200 text-slate-400"}`}>
              <span className={`h-5 w-5 rounded-full flex items-center justify-center text-xs ${i === step ? "bg-white/20" : i < step ? "bg-emerald-200" : "bg-slate-100"}`}>
                {i < step ? <CheckCircle2 size={13} /> : i + 1}
              </span>
              {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 0: Upload */}
      {step === 0 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 rounded-md border-slate-200 shadow-none lg:col-span-2">
            <div className="overline mb-4">1 · Redaktionsliste</div>
            <label data-testid="import-dropzone" className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-md py-12 cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors duration-150">
              <FileSpreadsheet size={36} className="text-slate-300 mb-3" />
              <span className="font-semibold text-slate-700">{file ? file.name : "Excel- oder CSV-Datei hierher ziehen"}</span>
              <span className="text-xs text-slate-400 mt-1">.xlsx, .xls oder .csv</span>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-file-input"
                onChange={(e) => setFile(e.target.files[0])} />
            </label>
          </Card>

          <Card className="p-6 rounded-md border-slate-200 shadow-none space-y-5">
            <div>
              <div className="overline mb-2">2 · Projekt / Kampagne</div>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="import-project-select"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-2">3 · Asset-Ort</div>
              <Select value={assetSource} onValueChange={setAssetSource}>
                <SelectTrigger data-testid="import-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload">Lokaler Upload / Medienbibliothek</SelectItem>
                  <SelectItem value="drive">Google Drive Ordner</SelectItem>
                  <SelectItem value="path">Serverpfad</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-2 flex items-start gap-1.5">
                {assetSource === "drive" ? <HardDriveDownload size={13} className="mt-0.5 shrink-0" /> : <FolderOpen size={13} className="mt-0.5 shrink-0" />}
                {assetSource === "drive"
                  ? "Google-Drive-Verbindung folgt (benötigt Google-Credentials). Aktuell werden Assets aus der Medienbibliothek gematcht."
                  : "Assets werden gegen die Medienbibliothek dieses Mandanten gematcht (Format & Serie automatisch erkannt)."}
              </p>
            </div>
            <div>
              <div className="overline mb-2">4 · Standard-Plattform</div>
              <Select value={defaultPlatform} onValueChange={setDefaultPlatform}>
                <SelectTrigger data-testid="import-default-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PLATFORM_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-2">Wird für Zeilen ohne eigene Plattform-Spalte verwendet.</p>
            </div>
            <Button data-testid="import-upload-btn" onClick={doUpload} disabled={busy || !file}
              className="w-full rounded-full bg-[#0A0A0A] hover:bg-[#212529]">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <>Datei analysieren <ArrowRight size={15} className="ml-1.5" /></>}
            </Button>
          </Card>
        </div>
      )}

      {/* Step 1: Mapping */}
      {step === 1 && imp && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 rounded-md border-slate-200 shadow-none lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Spalten zuordnen · {imp.row_count} Zeilen</div>
              {profiles.length > 0 && (
                <Select onValueChange={applyProfile}>
                  <SelectTrigger className="w-52 h-8 text-xs" data-testid="import-profile-select"><SelectValue placeholder="Importprofil laden" /></SelectTrigger>
                  <SelectContent>{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([field, label, req]) => (
                <div key={field}>
                  <Label className="text-xs font-semibold text-slate-600">{label} {req && <span className="text-rose-500">*</span>}
                    {mode === "multi" && ["platform", "caption", "link"].includes(field) && <span className="ml-1 text-[10px] text-indigo-500">(auto/Multi)</span>}
                  </Label>
                  <Select value={mapping[field] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v === "__none__" ? undefined : v }))}>
                    <SelectTrigger data-testid={`map-${field}`} className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— nicht zuordnen —</SelectItem>
                      {imp.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </Card>

          <div className="space-y-6">
            {sheetNames.length > 1 && (
              <Card className="p-5 rounded-md border-slate-200 shadow-none">
                <div className="overline mb-2 flex items-center gap-1.5"><Table2 size={13} /> Excel-Blatt</div>
                <Select value={usedSheet} onValueChange={switchSheet}>
                  <SelectTrigger data-testid="import-sheet-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{sheetNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-2">Automatisch das plan-artige Blatt gewählt – bei Bedarf umschalten.</p>
              </Card>
            )}
            {multi?.platform_captions && Object.keys(multi.platform_captions).length > 0 && (
              <Card className="p-5 rounded-md border-indigo-200 bg-indigo-50/40 shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline !text-indigo-600 flex items-center gap-1.5"><Layers size={13} /> Multi-Plattform erkannt</div>
                  <Switch data-testid="import-multi-toggle" checked={mode === "multi"} onCheckedChange={(v) => setMode(v ? "multi" : "single")} />
                </div>
                <p className="text-xs text-slate-500 mb-3">Pro Zeile wird je Plattform ein Post mit passender Caption, Format-Datei & Termin erzeugt.</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(multi.platform_captions).map((p) => <PlatformBadge key={p} platform={p} />)}
                </div>
                <div className="mt-2 text-[11px] text-slate-400 font-mono">Formate: {Object.keys(multi.format_files || {}).join(" · ") || "–"}</div>
              </Card>
            )}
            <Card className="p-5 rounded-md border-slate-200 shadow-none">
              <div className="overline mb-3">Vorschau (erste Zeilen)</div>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="text-[11px] font-mono w-full">
                  <thead><tr>{imp.headers.map((h) => <th key={h} className="text-left px-1.5 py-1 text-slate-400 whitespace-nowrap border-b">{h}</th>)}</tr></thead>
                  <tbody>
                    {imp.sample.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50">{imp.headers.map((h) => <td key={h} className="px-1.5 py-1 whitespace-nowrap max-w-[120px] truncate text-slate-600">{r[h]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card className="p-5 rounded-md border-slate-200 shadow-none">
              <div className="overline mb-2">Importprofil speichern</div>
              <Input data-testid="import-profile-name" placeholder="z. B. ComRocket A/B/C Template" value={profileName} onChange={(e) => setProfileName(e.target.value)} className="text-sm" />
              <p className="text-xs text-slate-400 mt-2">Optional: Mapping für spätere Importe wiederverwenden.</p>
            </Card>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && analysis && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <CountTile testid="count-total" label="Erkannt" value={c.total} icon={FileSpreadsheet} tone="neutral" />
            <CountTile testid="count-ready" label="Bereit" value={c.ready} icon={CheckCircle2} tone="ready" />
            <CountTile testid="count-warning" label="Mit Warnungen" value={c.warning} icon={AlertTriangle} tone="warn" />
            <CountTile testid="count-blocked" label="Blockiert" value={c.blocked} icon={XCircle} tone="block" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <CountTile testid="count-missing-asset" label="Fehlende Assets" value={c.missing_asset || 0} icon={Ban} tone="neutral" />
            <CountTile testid="count-format" label="Formatprobleme" value={c.format_issue || 0} icon={FileWarning} tone="neutral" />
            <CountTile testid="count-link" label="Fehlende Links" value={c.missing_link || 0} icon={Link2} tone="neutral" />
            <CountTile testid="count-schedule" label="Terminprobleme" value={c.schedule_issue || 0} icon={CalendarClock} tone="neutral" />
            <CountTile testid="count-duplicate" label="Duplikate" value={c.duplicate || 0} icon={Copy} tone="neutral" />
          </div>

          <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="text-left text-xs text-slate-500">
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold">Plattform</th>
                    <th className="px-4 py-2 font-semibold">Serie</th>
                    <th className="px-4 py-2 font-semibold">Asset</th>
                    <th className="px-4 py-2 font-semibold">Termin</th>
                    <th className="px-4 py-2 font-semibold">Caption</th>
                    <th className="px-4 py-2 font-semibold">Hinweise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {analysis.rows.map((r) => (
                    <tr key={r.id} data-testid={`import-row-${r.row_index}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2"><StatusBadge status={
                        ["veröffentlicht", "veroeffentlicht", "published", "gepostet"].includes((r.source_status || "").toLowerCase())
                          ? "veroeffentlicht"
                          : r.row_status === "ready" ? "bereit" : r.row_status === "warning" ? "geplant" : "blockiert"
                      } /></td>
                      <td className="px-4 py-2"><PlatformBadge platform={r.platform} /></td>
                      <td className="px-4 py-2 font-mono text-xs">{r.series || "–"}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.asset_filename
                          ? <span className="text-emerald-700">{r.asset_filename}</span>
                          : <span className="text-rose-600">{r.asset_ref || "—"}</span>}
                        {r.asset_format && <span className="ml-1 text-slate-400">[{r.asset_format}]</span>}
                      </td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap">{r.scheduled_at ? new Date(r.scheduled_at).toLocaleString("de-DE") : <span className="text-amber-600">fehlt</span>}</td>
                      <td className="px-4 py-2 max-w-[240px] truncate text-slate-600 text-xs">{r.caption}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-0.5">
                          {r.blockers.map((b, i) => <span key={i} className="text-[11px] text-rose-600 flex items-center gap-1"><XCircle size={11} />{b}</span>)}
                          {r.warnings.map((w, i) => <span key={i} className="text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle size={11} />{w}</span>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <Card className="p-10 rounded-md border-slate-200 shadow-none text-center max-w-lg mx-auto">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="text-emerald-600" size={30} />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2">Import bestätigt</h2>
          <p className="text-slate-500 text-sm mb-6">Die Posts wurden in Redaktionsplan & Queue übernommen.</p>
          {result.created_tiktok_ready > 0 && <div className="mb-6 space-y-2 text-sm text-left rounded-md border border-sky-200 bg-sky-50 p-3">
            <p>{result.tiktok_prepared > 0 ? `TikTok-Einstellungen für ${result.tiktok_prepared} Beiträge gespeichert. Queue starten aktiviert unveränderte Beiträge ohne zweite Bestätigung.` : "TikTok-Einstellungen jetzt einmal im Import bestätigen, damit beim Queue-Start keine zweite Freigabe nötig ist."}</p>
            <p>Der Import allein startet noch keine Veröffentlichung. LinkedIn bleibt manuell.</p>
            {canConfirmTikTok && <Button data-testid="import-tiktok-settings" variant="outline" onClick={() => setTiktokImportOpen(true)}>TikTok-Import-Freigabe öffnen</Button>}
            {!canConfirmTikTok && <p>Die TikTok-Freigabe benötigt einen Administrator.</p>}
          </div>}
          <div className="grid grid-cols-5 gap-3 mb-8">
            {[["Neu", result.created], ["Aktualisiert", result.updated], ["Unverändert", result.ignored], ["Blockiert", result.blocked_imported || 0], ["Übersprungen", result.skipped_published]].map(([l, v]) => (
              <div key={l} className="border border-slate-200 rounded-md p-3">
                <div className="font-display text-2xl font-extrabold">{v}</div>
                <div className="text-[11px] text-slate-400">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 justify-center">
            <Button data-testid="import-goto-plan" onClick={() => navigate("/plan")} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529]">Zum Redaktionsplan</Button>
            <Button variant="outline" onClick={() => { setStep(0); setImp(null); setAnalysis(null); setResult(null); setFile(null); setTiktokImportOpen(false); }} className="rounded-full">Weiterer Import</Button>
          </div>
        </Card>
      )}

      {/* Sticky footer nav */}
      {step > 0 && step < 3 && (
        <div className="fixed bottom-0 left-64 right-0 bg-white/80 backdrop-blur-xl border-t border-slate-200 px-6 py-3 flex items-center justify-between z-40">
          <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="wizard-back"><ArrowLeft size={15} className="mr-1.5" /> Zurück</Button>
          {step === 1 && (
            <Button data-testid="wizard-analyze" onClick={doAnalyze} disabled={busy} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529]">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <>Analysieren & Matchen <ArrowRight size={15} className="ml-1.5" /></>}
            </Button>
          )}
          {step === 2 && (
            <div className="flex items-center gap-3">
              {c.blocked > 0 && <span className="text-xs text-amber-600 flex items-center gap-1"><Ban size={13} /> {c.blocked} blockierte Zeilen werden als Status „blockiert" importiert</span>}
              <Button data-testid="wizard-confirm" onClick={doConfirm} disabled={busy || c.total === 0} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529]">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Import bestätigen ({c.total} Posts) <CheckCircle2 size={15} className="ml-1.5" /></>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
